import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import BookingCalendar from "./BookingCalendar";
import IntakeForm from "./IntakeForm";
import PaymentStep from "./PaymentStep";
import Confirmation from "./Confirmation";
import { Icon } from "./Icons";
import { findTherapist } from "./therapists";
import "./Therapy.css";
import "./TherapyPages.css";

function TherapyBooking() {
    const { slug } = useParams();
    const therapistFromSlug = slug ? findTherapist(slug) : null;

    const [bookingState, setBookingState] = useState("booking");
    const [bookingData, setBookingData] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [bookingState]);

    // The "active" therapist is either the one preselected via URL slug
    // OR the one chosen in BookingCalendar's therapist-pick step.
    // When bookingData exists, it has the authoritative therapist.
    const activeTherapist = bookingData?.therapist || therapistFromSlug;

    const handleBookingComplete = (data) => {
        setBookingData(data);
        setBookingState("intake");
    };

    const handleIntakeComplete = () => {
        if (bookingData.service.id === "consultation") {
            setBookingState("confirmed");
        } else {
            setBookingState("payment");
        }
    };

    const handlePaymentComplete = () => {
        setBookingState("confirmed");
    };

    const resetBooking = () => {
        setBookingState("booking");
        setBookingData(null);
    };

    const headerContent = {
        booking: {
            eyebrow: therapistFromSlug
                ? `Booking with ${therapistFromSlug.name}`
                : "Book a session",
            title: "Schedule a session.",
            lede: therapistFromSlug
                ? "Pick a service and time that work for you. After booking, you'll complete a brief pre-session questionnaire and (for paid sessions) payment details."
                : "Pick a service, choose your therapist, and select a time. Each step shows you what's actually available — no calling around.",
        },
        intake: {
            eyebrow: "Pre-session questionnaire",
            title: "A few quick questions.",
            lede: activeTherapist
                ? `Tailored for ${activeTherapist.name.split(" ").slice(-1)[0]}'s practice. Full clinical intake comes later through the secure portal.`
                : "Helps us prepare for your session. Full clinical intake comes later through the secure portal.",
        },
        payment: {
            eyebrow: "Payment",
            title: "Payment details.",
            lede: "Your card is held for the session and charged at the time of the appointment, per the cancellation policy.",
        },
    };

    const currentHeader = headerContent[bookingState];

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            {bookingState !== "confirmed" && currentHeader && (
                <section className="page-header">
                    <div className="demo-container">
                        <p className="demo-eyebrow">{currentHeader.eyebrow}</p>
                        <h1>{currentHeader.title}</h1>
                        <p className="page-lede">{currentHeader.lede}</p>

                        {therapistFromSlug && bookingState === "booking" && (
                            <div className="booking-therapist-banner">
                                <div
                                    className="mini-portrait"
                                    style={{
                                        background: `linear-gradient(135deg, ${therapistFromSlug.portraitColors[0]}, ${therapistFromSlug.portraitColors[1]})`,
                                    }}
                                >
                                    {therapistFromSlug.name
                                        .replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s+/, "")
                                        .split(" ")
                                        .map((p) => p[0])
                                        .slice(0, 2)
                                        .join("")}
                                </div>
                                <div className="booking-therapist-info">
                                    <span className="booking-therapist-label">Booking with</span>
                                    <span className="booking-therapist-name">
                                        {therapistFromSlug.name}, {therapistFromSlug.credential}
                                    </span>
                                </div>
                                <Link
                                    to={path(`therapists/${therapistFromSlug.slug}`)}
                                    className="booking-therapist-link"
                                >
                                    View profile
                                </Link>
                            </div>
                        )}

                        {bookingState === "booking" && (
                            <div className="booking-trust-row">
                                <div className="booking-trust-item">
                                    <Icon name="lock" />
                                    <span>Secure booking</span>
                                </div>
                                <div className="booking-trust-item">
                                    <Icon name="clock" />
                                    <span>24-hr cancellation policy</span>
                                </div>
                                <div className="booking-trust-item">
                                    <Icon name="check" />
                                    <span>Free consultation available</span>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {bookingState === "booking" && (
                <section className="demo-section">
                    <div className="demo-container">
                        <BookingCalendar
                            therapist={therapistFromSlug}
                            onBookingComplete={handleBookingComplete}
                        />
                    </div>
                </section>
            )}

            {bookingState === "intake" && (
                <section className="demo-section">
                    <div className="demo-container">
                        <IntakeForm
                            bookingData={bookingData}
                            therapist={activeTherapist}
                            onComplete={handleIntakeComplete}
                        />
                    </div>
                </section>
            )}

            {bookingState === "payment" && (
                <section className="demo-section">
                    <div className="demo-container">
                        <PaymentStep
                            bookingData={bookingData}
                            therapist={activeTherapist}
                            onComplete={handlePaymentComplete}
                        />
                    </div>
                </section>
            )}

            {bookingState === "confirmed" && (
                <section className="demo-section confirmation-wrap">
                    <div className="demo-container">
                        <Confirmation
                            bookingData={bookingData}
                            therapist={activeTherapist}
                        />
                        <div className="confirmation-actions-row">
                            <button className="btn-secondary" onClick={resetBooking}>
                                Book another session
                            </button>
                            <Link to={path()} className="btn-secondary">
                                Return home
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            <TherapyFooter />
        </main>
    );
}

export default TherapyBooking;